import { DropdownMenuItemType, Icon, IDropdownOption } from "@fluentui/react";
import { baseUrlUser, shareUrl } from "../../constants/url.constant";
import {
    PriceDisplayType, ProductFeatureType, ProductSearchOption, SearchOption
} from "./models/ProductModel";
import { IDataResult } from "../common/models/Result";
import { DataUtils, dropdownOptionCallback } from "../common/views/DataUtils";
import { Utils } from "../common/views/Utils";
import { SortDirection } from "../home/models/PageTypes";
import { ProductAccessoryService } from "./services/ProductAccessoryService";
import { ShortOrderItemModel } from "../orders/models/OrderModel";
import { CustomerLevel } from "../customers/models/CustomerModel";

export class ProductUtils {
    public static onShare = async (lineId: number) => {
        const url = shareUrl + `?type=0&&option=${ProductSearchOption.lineId}&&searchText=${lineId}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: document.title,
                    text: 'Check this out',
                    url: url
                });
            } catch (error) {
                console.log('Share cancelled');
            }
        } else {
            await navigator.clipboard.writeText(url);
            alert('Link copied!');
        }
    }

    public static printStarIcon(cssColor = "firmed") {
        return <Icon iconName="FavoriteStarFill" className={cssColor} />;
    }

    public static splitSpecification(text: string) {
        return !Utils.isNullOrEmpty(text) ? text.split("\n") : [];
    }

    public static getProductText(lineName?: string, sizeName?: string, code?: string, separator: string = "\u2022") {
        var texts = [];

        if (!Utils.isNullOrEmpty(lineName)) texts.push(lineName);
        if (!Utils.isNullOrEmpty(sizeName)) texts.push(sizeName);
        if (!Utils.isNullOrEmpty(code)) texts.push(code);

        if (texts.length <= 0) return "";

        return texts.join(" " + separator + " ");
    }

    public static getImageUrl(imageUrl?: string) {
        var url = !Utils.isNullOrEmpty(imageUrl) ? baseUrlUser + imageUrl : "";

        if (!Utils.isNullOrEmpty(url)) {
            return url.replace("/Products/", "/Products/Thumbnails/");
        }

        return url;
    }

    public static getSortOptions() {
        const list: any[] = [
            { key: 0, name: "", direction: SortDirection.ascending, text: "" },
            { key: 1, name: "Brand", direction: SortDirection.ascending, text: "Brand" },
            { key: 2, name: "ProductName", direction: SortDirection.ascending, text: "Name" },
            { key: 3, name: "Style", direction: SortDirection.ascending, text: "Style" },
            { key: 4, name: "NewArrivalDate", direction: SortDirection.descending, text: "Newest -> Oldest" },
            { key: 5, name: "NewArrivalDate", direction: SortDirection.ascending, text: "Oldest -> Newest" },
            { key: 6, name: "Id", direction: SortDirection.ascending, text: "ID (Ascending)" },
            { key: 7, name: "Id", direction: SortDirection.descending, text: "ID (Descending)" }
        ];

        return list;
    }

    public static getFeatureOptions() {
        const list: any[] = [
            { key: "", text: "" },
            { key: ProductFeatureType.choice, text: "Best Choice" },
            { key: ProductFeatureType.newArrival, text: "New Arrival" },
            { key: ProductFeatureType.specialSale, text: "Spcial Sale" },
            { key: ProductFeatureType.topRate, text: "Top Rate" },
            { key: ProductFeatureType.topSale, text: "Top Sale" }
        ];

        return list;
    }

    public static getCustomerLevelOptions() {
        const list: any[] = [
            { key: "", text: "" },
            { key: CustomerLevel.none, text: "None" },
            { key: CustomerLevel.regular, text: "Regular" },
            { key: CustomerLevel.invited, text: "Invited" },
            { key: CustomerLevel.trusted, text: "Trusted" },
            { key: CustomerLevel.oneTime, text: "One Time" }
        ];

        return list;
    }
    public static getPriceDisplayOptions() {
        return [
            { key: PriceDisplayType.hide, text: "Hide Price" },
            { key: PriceDisplayType.show, text: "Show Price" },
            { key: PriceDisplayType.xOne, text: "Hide with x" },
            { key: PriceDisplayType.xTwo, text: "Hide with 2x" },
            { key: PriceDisplayType.xThree, text: "Hide with 3x" },
            { key: PriceDisplayType.xFour, text: "Hide with 4x" },
            { key: PriceDisplayType.xFive, text: "Hide with 5x" }
        ];
    }

    public static getSearchOptions(): IDropdownOption[] {
        let list: IDropdownOption[] = [];

        list.push({ key: SearchOption.contains, text: "Contains" });
        list.push({ key: SearchOption.exact, text: "Exact" });
        list.push({ key: SearchOption.lineId, text: "Line ID" });
        list.push({ key: SearchOption.groupId, text: "Group ID" });
        list.push({ key: SearchOption.barcode, text: "Barcode" });

        return list;
    }

    public static getBrandOptions(callback: dropdownOptionCallback) {
        const onResult = (result: IDataResult) => {
            const data = result.data;
            let list: IDropdownOption[] = [];

            for (var i = 0; i < data.length; i++) {
                list.push({ key: data[i].id, text: data[i].name });
            }

            callback(list);
        };

        const service = new ProductAccessoryService();
        const request = service.getBrands();
        DataUtils.onRequest(request, onResult);
    }

    public static getSizeOptions(callback: dropdownOptionCallback) {
        const onResult = (result: IDataResult) => {
            const data = result.data;
            let list: IDropdownOption[] = [];

            list.push({ key: 0, text: "Select..." });

            let type = "";
            let dividerKey = 1000;

            for (var i = 0; i < data.length; i++) {
                const item = data[i];
                if (type.length > 0 && type !== item.type) {
                    list.push({ key: dividerKey++, text: "-", itemType: DropdownMenuItemType.Divider });
                }

                const text = item.nickName + (item.nickName === item.name ? "" : " - " + item.name);
                list.push({ key: item.id, text: text });
            }

            callback(list);
        };

        const service = new ProductAccessoryService();
        const request = service.getSizes();
        DataUtils.onRequest(request, onResult);
    }

    public static getSubInfoForOrder(order: ShortOrderItemModel): string {
        return this.getSubInfo(order.lineName, order.size, order.code);
    }

    public static getSubInfo(lineName: string, size: string, code: string): string {
        var list: string[] = [];

        if (!Utils.isNullOrEmpty(lineName)) list.push(lineName);
        if (!Utils.isNullOrEmpty(size)) list.push(size);
        if (!Utils.isNullOrEmpty(code)) list.push(code);

        if (list.length > 0) return list.join(" - ");

        return "";
    }
}